// Assets & Attachments — the main workspace of a work item. Files/links grouped
// by purpose (reference in, deliverables out, supporting extras), each with a
// version chain. Managed files DOWNLOAD; external links OPEN (a Drive/Figma link
// is not necessarily a direct download).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Compass, Download, ExternalLink, FileText, History, Link2 as LinkIcon, Loader2, MoreHorizontal, Paperclip, Plus, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { launchWeb } from '@/features/web/lib/launch';
import { Button, buttonVariants } from '@/features/shared/components/ui/button';
import { cn } from '@/lib/utils';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { DataState } from '@/features/shared/components/ui/DataState';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { fmtDT } from '../workassignments/shared';
import {
  AssetPurpose, WorkAsset, WorkAssetVersion,
  listWorkAssets, addWorkAssetLink, uploadWorkAsset, addWorkAssetLinkVersion, addWorkAssetFileVersion,
  updateWorkAsset, archiveWorkAsset, downloadWorkAssetVersion, uploadWorkAttachments,
} from '@/services/workService';

const PROVIDER_LABEL: Record<string, string> = {
  kazipeople: 'File', google_drive: 'Google Drive', onedrive: 'OneDrive',
  sharepoint: 'SharePoint', dropbox: 'Dropbox', figma: 'Figma', adobe: 'Adobe', external: 'External Asset',
};

const GROUPS: { key: AssetPurpose; label: string; hint: string }[] = [
  { key: 'reference', label: 'Reference assets', hint: 'Material provided to help complete the work.' },
  { key: 'deliverable', label: 'Deliverables', hint: 'Output produced by the assignee.' },
  { key: 'supporting', label: 'Supporting files', hint: 'Additional supporting materials.' },
];

export const isManaged = (v?: WorkAssetVersion) => v?.type === 'uploaded_file';

// The action a version affords: managed files DOWNLOAD; external links OPEN (a
// Drive/Figma link is not necessarily a direct download). Drives the button label.
export const assetActionLabel = (v?: WorkAssetVersion): 'download' | 'open' =>
  isManaged(v) ? 'download' : 'open';

export const WorkAssetsSection: React.FC<{
  workItemId: string;
  canEdit: boolean;
  /** human title of the work item, so "Open in Web" can carry a real context label. */
  workItemTitle?: string;
  /** default purpose for the primary Upload/Add-link actions (assignee → deliverable). */
  defaultPurpose?: AssetPurpose;
  onChange?: () => void;
}> = ({ workItemId, canEdit, workItemTitle, defaultPurpose = 'reference', onChange }) => {
  const { t } = useTranslation('work');
  const confirm = useConfirm();
  const navigate = useNavigate();
  const loc = useLocation();

  // Open an external-link asset INSIDE KaziPeople Web, connected to this work item —
  // instead of a bare new tab. The browser keeps the work context + a way back.
  const openInWeb = (a: WorkAsset) => {
    const url = a.current?.url;
    if (!url) return;
    launchWeb(navigate, loc.pathname, {
      url,
      context: { type: 'work_item', id: workItemId, title: workItemTitle },
    });
  };
  const [assets, setAssets] = useState<WorkAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  // link dialog: 'new:<purpose>' to create, or an assetId for a new version.
  const [linkTarget, setLinkTarget] = useState<string | null>(null);
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkPurpose, setLinkPurpose] = useState<AssetPurpose>(defaultPurpose);
  const [versionsOf, setVersionsOf] = useState<WorkAsset | null>(null);
  const [renaming, setRenaming] = useState<WorkAsset | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const versionUploadRef = useRef<HTMLInputElement | null>(null);
  const uploadPurpose = useRef<AssetPurpose>(defaultPurpose);
  const versionTarget = useRef<string | null>(null);

  const load = useCallback(() => {
    setLoading(true); setErr(false);
    listWorkAssets(workItemId).then(setAssets).catch(() => setErr(true)).finally(() => setLoading(false));
  }, [workItemId]);
  useEffect(() => { load(); }, [load]);
  const reload = () => { load(); onChange?.(); };

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); toast.success(ok); reload(); }
    catch (e) { toast.error(getApiErrorMessage(e, t('asset_action_failed', { defaultValue: 'Something went wrong.' }))); }
    finally { setBusy(false); }
  };

  // ---- file uploads (new asset + new version) via hidden inputs ----
  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    void run(() => uploadWorkAsset(workItemId, file, { purpose: uploadPurpose.current }), t('asset_added', { defaultValue: 'Asset added.' }) as string);
  };
  const pickVersionUpload = (assetId: string) => { versionTarget.current = assetId; setMenuFor(null); versionUploadRef.current?.click(); };
  const onVersionUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    const assetId = versionTarget.current;
    if (!file || !assetId) return;
    void run(() => addWorkAssetFileVersion(workItemId, assetId, file), t('asset_version_added', { defaultValue: 'New version added.' }) as string);
  };

  // ---- external links (new asset + new version) ----
  const submitLink = () => {
    const url = linkUrl.trim();
    if (!/^https?:\/\/.+/i.test(url)) { toast.error(t('asset_url_invalid', { defaultValue: 'Enter an http(s) link.' })); return; }
    const target = linkTarget!;
    const done = () => { setLinkTarget(null); setLinkName(''); setLinkUrl(''); };
    if (target.startsWith('new:')) {
      const purpose = target.slice(4) as AssetPurpose;
      void run(() => addWorkAssetLink(workItemId, { name: linkName.trim() || url, url, purpose }), t('asset_added', { defaultValue: 'Asset added.' }) as string).then(done);
    } else {
      void run(() => addWorkAssetLinkVersion(workItemId, target, { url }), t('asset_version_added', { defaultValue: 'New version added.' }) as string).then(done);
    }
  };

  const openOrDownload = (a: WorkAsset, v?: WorkAssetVersion) => {
    const ver = v || a.current;
    if (!ver) return;
    if (isManaged(ver)) {
      void downloadWorkAssetVersion(workItemId, a.id, ver.id, ver.file_name).catch((e) =>
        toast.error(getApiErrorMessage(e, t('asset_download_failed', { defaultValue: 'Could not download.' }))));
    } else if (ver.url) {
      window.open(ver.url, '_blank', 'noopener,noreferrer');
    }
  };

  const changePurpose = (a: WorkAsset, purpose: AssetPurpose) => {
    setMenuFor(null);
    void run(() => updateWorkAsset(workItemId, a.id, { purpose }), t('asset_updated', { defaultValue: 'Asset updated.' }) as string);
  };
  const doRename = () => {
    if (!renaming) return;
    const name = renameVal.trim();
    if (!name) return;
    const a = renaming; setRenaming(null);
    void run(() => updateWorkAsset(workItemId, a.id, { name }), t('asset_updated', { defaultValue: 'Asset updated.' }) as string);
  };
  const remove = async (a: WorkAsset) => {
    setMenuFor(null);
    if (!(await confirm({ title: t('remove_asset', { defaultValue: 'Remove asset' }), message: t('remove_asset_confirm', { defaultValue: 'Remove "{{name}}"? Version history is kept.', name: a.name }), confirmLabel: t('common.remove', { defaultValue: 'Remove' }), danger: true }))) return;
    void run(() => archiveWorkAsset(workItemId, a.id), t('asset_removed', { defaultValue: 'Asset removed.' }) as string);
  };

  if (loading) return <DataState variant="loading" compact />;
  if (err) return <DataState variant="error" compact onRetry={load} />;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-h4 text-charcoal">{t('assets_title', { defaultValue: 'Assets & attachments' })}</h2>
          <p className="text-xs text-muted-foreground">{t('assets_sub', { defaultValue: 'Files, designs and resources related to this work.' })}</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button type="button" size="sm" onClick={() => setAttachOpen(true)} disabled={busy}>
              <Paperclip className="w-4 h-4 mr-1" /> {t('attach_files', { defaultValue: 'Attach files' })}
            </Button>
            {/* A real <label> around a labelled, visually-hidden (not display:none)
                file input: a click on the label opens the chooser natively, the
                input is keyboard-focusable (Space/Enter), and it is addressable
                by name. The old ref.click() on an unlabelled display:none input
                was reported dead (X13). */}
            <label className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'cursor-pointer focus-within:ring-2 focus-within:ring-orange-500/40',
              busy && 'pointer-events-none opacity-50')}>
              <Upload className="w-4 h-4 mr-1" /> {t('upload_file', { defaultValue: 'Upload file' })}
              <input type="file" className="sr-only" disabled={busy}
                aria-label={t('upload_file', { defaultValue: 'Upload file' }) as string}
                onChange={(e) => { uploadPurpose.current = defaultPurpose; onUpload(e); }} />
            </label>
            <Button type="button" variant="outline" size="sm" onClick={() => { setLinkPurpose(defaultPurpose); setLinkTarget(`new:${defaultPurpose}`); }} disabled={busy}>
              <LinkIcon className="w-4 h-4 mr-1" /> {t('add_link', { defaultValue: 'Add asset link' })}
            </Button>
          </div>
        )}
      </div>

      {/* Driven from an asset's menu ("Upload new version"); labelled so it is
          addressable, kept out of the tab order since the menu item is the control. */}
      <input ref={versionUploadRef} type="file" className="sr-only" tabIndex={-1}
        aria-label={t('upload_new_version', { defaultValue: 'Upload new version' }) as string} onChange={onVersionUpload} />

      {assets.length === 0 ? (
        <DataState variant="empty" compact title={t('no_assets', { defaultValue: 'No assets yet' })}
          description={canEdit ? t('no_assets_hint', { defaultValue: 'Upload a file or add a link to get started.' }) as string : undefined} />
      ) : (
        GROUPS.map((g) => {
          const inGroup = assets.filter((a) => a.purpose === g.key);
          if (inGroup.length === 0) return null;
          return (
            <div key={g.key} className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t(`group_${g.key}`, { defaultValue: g.label })}</div>
              <div className="grid gap-2 sm:grid-cols-2">
                {inGroup.map((a) => (
                  <AssetCard key={a.id} a={a} canEdit={canEdit} busy={busy} menuOpen={menuFor === a.id}
                    onMenu={() => setMenuFor(menuFor === a.id ? null : a.id)}
                    onOpen={() => openOrDownload(a)} onOpenInWeb={() => openInWeb(a)} onHistory={() => setVersionsOf(a)}
                    onNewFileVersion={() => pickVersionUpload(a.id)}
                    onNewLinkVersion={() => { setMenuFor(null); setLinkTarget(a.id); setLinkName(''); setLinkUrl(''); }}
                    onRename={() => { setMenuFor(null); setRenaming(a); setRenameVal(a.name); }}
                    onPurpose={(p) => changePurpose(a, p)} onRemove={() => remove(a)} t={t} />
                ))}
              </div>
            </div>
          );
        })
      )}

      {/* Add link / new link-version dialog */}
      {linkTarget && (
        <Modal onClose={() => setLinkTarget(null)}>
          <h3 className="text-h4 text-charcoal">{linkTarget.startsWith('new:') ? t('add_link', { defaultValue: 'Add asset link' }) : t('new_link_version', { defaultValue: 'Add a new link version' })}</h3>
          {linkTarget.startsWith('new:') && (
            <div><Label>{t('asset_name', { defaultValue: 'Asset name' })}</Label><Input value={linkName} onChange={(e) => setLinkName(e.target.value)} placeholder="Campaign Source Files" /></div>
          )}
          <div><Label>{t('asset_url', { defaultValue: 'URL' })}</Label><Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://drive.google.com/…" /></div>
          {linkTarget.startsWith('new:') && (
            <div>
              <Label>{t('purpose', { defaultValue: 'Purpose' })}</Label>
              <select className="w-full rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2"
                value={linkPurpose} onChange={(e) => { setLinkPurpose(e.target.value as AssetPurpose); setLinkTarget(`new:${e.target.value}`); }}>
                <option value="reference">{t('purpose_reference', { defaultValue: 'Reference' })}</option>
                <option value="deliverable">{t('purpose_deliverable', { defaultValue: 'Deliverable' })}</option>
                <option value="supporting">{t('purpose_supporting', { defaultValue: 'Supporting' })}</option>
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setLinkTarget(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={submitLink} disabled={busy}>{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{t('common.add', { defaultValue: 'Add' })}</Button>
          </div>
        </Modal>
      )}

      {/* Rename dialog */}
      {renaming && (
        <Modal onClose={() => setRenaming(null)}>
          <h3 className="text-h4 text-charcoal">{t('rename_asset', { defaultValue: 'Rename asset' })}</h3>
          <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setRenaming(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={doRename} disabled={busy}>{t('common.save', { defaultValue: 'Save' })}</Button>
          </div>
        </Modal>
      )}

      {attachOpen && (
        <AttachFilesDialog
          workItemId={workItemId}
          defaultPurpose={defaultPurpose}
          onClose={() => setAttachOpen(false)}
          onDone={() => { setAttachOpen(false); reload(); }}
        />
      )}

      {/* Version history */}
      {versionsOf && (
        <Modal onClose={() => setVersionsOf(null)}>
          <h3 className="text-h4 text-charcoal">{t('version_history', { defaultValue: 'Version history' })} — {versionsOf.name}</h3>
          <ul className="divide-y divide-border max-h-[50vh] overflow-y-auto">
            {(versionsOf.versions || []).map((v) => (
              <li key={v.id} className="flex items-center gap-2 py-2 text-sm">
                <span className="font-medium">v{v.version}</span>
                {v.version === versionsOf.current_version && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-success-soft text-success-strong">{t('current', { defaultValue: 'Current' })}</span>}
                <span className="text-xs text-muted-foreground">{PROVIDER_LABEL[v.provider] || v.provider}</span>
                <span className="text-xs text-muted-foreground ml-auto">{fmtDT(v.created_at)}</span>
                <Button variant="ghost" size="sm" onClick={() => openOrDownload(versionsOf, v)}>
                  {isManaged(v) ? <Download className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex justify-end"><Button variant="ghost" onClick={() => setVersionsOf(null)}>{t('common.close', { defaultValue: 'Close' })}</Button></div>
        </Modal>
      )}
    </div>
  );
};

const AssetCard: React.FC<{
  a: WorkAsset; canEdit: boolean; busy: boolean; menuOpen: boolean;
  onMenu: () => void; onOpen: () => void; onOpenInWeb: () => void; onHistory: () => void;
  onNewFileVersion: () => void; onNewLinkVersion: () => void; onRename: () => void;
  onPurpose: (p: AssetPurpose) => void; onRemove: () => void; t: (k: string, o?: any) => string;
}> = ({ a, canEdit, busy, menuOpen, onMenu, onOpen, onOpenInWeb, onHistory, onNewFileVersion, onNewLinkVersion, onRename, onPurpose, onRemove, t }) => {
  const v = a.current;
  const managed = isManaged(v);
  const isExternalLink = !managed && !!v?.url;
  const provider = PROVIDER_LABEL[v?.provider || 'external'] || 'External Asset';
  const multi = a.current_version > 1;
  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2 relative">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 shrink-0 text-muted-foreground">{managed ? <FileText className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}</div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground line-clamp-2 break-words" title={a.name}>{a.name}</div>
          <div className="text-xs text-muted-foreground">
            {managed ? provider : `${provider} · ${t('external_asset', { defaultValue: 'External Asset' })}`}
            {managed && v?.size_bytes ? ` · ${fmtBytes(v.size_bytes)}` : ''}
          </div>
          {a.description && <div className="text-xs text-muted-foreground line-clamp-2 break-words">{a.description}</div>}
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {t('added', { defaultValue: 'Added' })} {fmtDT(a.created_at)}
            {multi ? ` · ${t('version', { defaultValue: 'Version' })} ${a.current_version} · ${t('current', { defaultValue: 'Current' })}` : ''}
          </div>
        </div>
        {canEdit && (
          <button type="button" aria-label="More" onClick={onMenu} className="shrink-0 text-muted-foreground hover:text-foreground p-1"><MoreHorizontal className="w-4 h-4" /></button>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={onOpen} disabled={busy}>
          {managed ? <><Download className="w-4 h-4 mr-1" />{t('download', { defaultValue: 'Download' })}</> : <><ExternalLink className="w-4 h-4 mr-1" />{t('open_asset', { defaultValue: 'Open Asset' })}</>}
        </Button>
        {isExternalLink && (
          <Button variant="ghost" size="sm" onClick={onOpenInWeb} disabled={busy} title={t('open_in_web_hint', { defaultValue: 'Open inside KaziPeople Web, connected to this work' }) as string}>
            <Compass className="w-4 h-4 mr-1" />{t('open_in_web', { defaultValue: 'Open in Web' })}
          </Button>
        )}
        {multi && (
          <Button variant="ghost" size="sm" onClick={onHistory}><History className="w-4 h-4 mr-1" />{t('version_history', { defaultValue: 'Version history' })}</Button>
        )}
      </div>
      {menuOpen && canEdit && (
        <div className="absolute right-2 top-9 z-10 w-56 rounded-md border border-border bg-popover shadow-md p-1 text-sm">
          <button type="button" className="w-full text-left px-2 py-1.5 rounded hover:bg-muted flex items-center gap-2" onClick={onNewFileVersion}><Upload className="w-4 h-4" />{t('upload_new_version', { defaultValue: 'Upload new version' })}</button>
          <button type="button" className="w-full text-left px-2 py-1.5 rounded hover:bg-muted flex items-center gap-2" onClick={onNewLinkVersion}><LinkIcon className="w-4 h-4" />{t('replace_link', { defaultValue: 'Add link version' })}</button>
          <button type="button" className="w-full text-left px-2 py-1.5 rounded hover:bg-muted flex items-center gap-2" onClick={onHistory}><History className="w-4 h-4" />{t('version_history', { defaultValue: 'Version history' })}</button>
          <button type="button" className="w-full text-left px-2 py-1.5 rounded hover:bg-muted flex items-center gap-2" onClick={onRename}><Paperclip className="w-4 h-4" />{t('rename', { defaultValue: 'Rename' })}</button>
          <div className="px-2 py-1 text-[11px] text-muted-foreground">{t('purpose', { defaultValue: 'Purpose' })}</div>
          {(['reference', 'deliverable', 'supporting'] as AssetPurpose[]).map((p) => (
            <button key={p} type="button" className={`w-full text-left px-2 py-1.5 rounded hover:bg-muted ${a.purpose === p ? 'text-primary-500 font-medium' : ''}`} onClick={() => onPurpose(p)}>{t(`purpose_${p}`, { defaultValue: p })}</button>
          ))}
          <button type="button" className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-red-600 flex items-center gap-2" onClick={onRemove}><X className="w-4 h-4" />{t('common.remove', { defaultValue: 'Remove' })}</button>
        </div>
      )}
    </div>
  );
};

// Limits mirror work-management-service (UploadAssets): 10 files, 25 MB each,
// 50 MB together, a 2000-character note. The server re-checks every one.
export const ATTACH_MAX_FILES = 10;
export const ATTACH_MAX_FILE_BYTES = 25 * 1024 * 1024;
export const ATTACH_MAX_TOTAL_BYTES = 50 * 1024 * 1024;
export const ATTACH_MAX_MESSAGE = 2000;

/** Why this selection can't be sent yet (null = it can). Pure, for tests. */
export function attachSelectionProblem(files: { size: number }[], message: string): 'none' | 'too_many' | 'file_too_large' | 'total_too_large' | 'message_too_long' | null {
  if (files.length === 0) return 'none';
  if (files.length > ATTACH_MAX_FILES) return 'too_many';
  if (files.some((f) => f.size > ATTACH_MAX_FILE_BYTES)) return 'file_too_large';
  if (files.reduce((n, f) => n + f.size, 0) > ATTACH_MAX_TOTAL_BYTES) return 'total_too_large';
  if (message.trim().length > ATTACH_MAX_MESSAGE) return 'message_too_long';
  return null;
}

const newUploadId = (): string => {
  try { return crypto.randomUUID(); } catch { return ''; }
};

// Attach one or more documents with an optional message. One submit = one
// upload operation = one grouped notification to the item's participants (never
// the uploader). The note becomes a Discussion comment linked to the files. A
// failed submit can be retried safely: the same upload id is reused, so the
// server never records it twice.
const AttachFilesDialog: React.FC<{
  workItemId: string;
  defaultPurpose: AssetPurpose;
  onClose: () => void;
  onDone: () => void;
}> = ({ workItemId, defaultPurpose, onClose, onDone }) => {
  const { t } = useTranslation('work');
  const [files, setFiles] = useState<File[]>([]);
  const [message, setMessage] = useState('');
  const [purpose, setPurpose] = useState<AssetPurpose>(defaultPurpose);
  const [sending, setSending] = useState(false);
  const uploadId = useRef<string>(newUploadId());
  const pickRef = useRef<HTMLInputElement | null>(null);
  const problem = attachSelectionProblem(files, message);
  const problemText: Record<string, string> = {
    too_many: t('attach_too_many', { defaultValue: 'Attach at most {{n}} files at a time.', n: ATTACH_MAX_FILES }) as string,
    file_too_large: t('attach_file_too_large', { defaultValue: 'Each file must be 25 MB or smaller — share large files as a link instead.' }) as string,
    total_too_large: t('attach_total_too_large', { defaultValue: 'These files are larger than 50 MB together — attach fewer at a time.' }) as string,
    message_too_long: t('attach_message_too_long', { defaultValue: 'The message is limited to {{n}} characters.', n: ATTACH_MAX_MESSAGE }) as string,
  };

  const add = (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list);
    setFiles((cur) => {
      const key = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
      const have = new Set(cur.map(key));
      return [...cur, ...picked.filter((f) => !have.has(key(f)))];
    });
    // A new selection is a new operation; a retry of the same selection is not.
    uploadId.current = newUploadId();
  };

  const submit = async () => {
    if (problem || sending) return;
    setSending(true);
    try {
      const res = await uploadWorkAttachments(workItemId, files, { purpose, message, clientUploadId: uploadId.current || undefined });
      const n = res.assets?.length ?? files.length;
      toast.success(t('attach_done', { defaultValue: '{{count}} file(s) attached. People on this work have been notified.', count: n }) as string);
      onDone();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('attach_failed', { defaultValue: 'The files couldn’t be attached. Try again.' }) as string));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal onClose={sending ? () => undefined : onClose}>
      <h3 className="text-h4 text-charcoal">{t('attach_title', { defaultValue: 'Attach documents' })}</h3>
      <p className="text-xs text-muted-foreground">
        {t('attach_hint', { defaultValue: 'Everyone working on this item will be notified. You won’t be.' })}
      </p>
      <input ref={pickRef} type="file" multiple className="hidden" aria-label={t('attach_choose', { defaultValue: 'Choose files' }) as string}
        onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
      <Button type="button" variant="outline" size="sm" onClick={() => pickRef.current?.click()} disabled={sending}>
        <Upload className="w-4 h-4 mr-1" /> {t('attach_choose', { defaultValue: 'Choose files' })}
      </Button>
      {files.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('attach_none', { defaultValue: 'No files chosen yet.' })}</p>
      ) : (
        <ul className="max-h-40 overflow-y-auto divide-y divide-border rounded-md border border-border">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-2 py-1.5 text-sm">
              <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
              <span className="text-xs text-muted-foreground">{fmtBytes(f.size)}</span>
              <button type="button" disabled={sending} aria-label={t('attach_remove_file', { defaultValue: 'Remove {{name}}', name: f.name }) as string}
                onClick={() => { setFiles((cur) => cur.filter((_, j) => j !== i)); uploadId.current = newUploadId(); }}
                className="p-1 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
      <div>
        <Label htmlFor={`attach-msg-${workItemId}`}>{t('attach_message', { defaultValue: 'Message (optional)' })}</Label>
        <Textarea id={`attach-msg-${workItemId}`} rows={3} value={message} disabled={sending} maxLength={ATTACH_MAX_MESSAGE + 1}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('attach_message_ph', { defaultValue: 'e.g. Please review before Friday.' }) as string} />
        <p className="text-[11px] text-muted-foreground text-right tabular-nums">{message.trim().length}/{ATTACH_MAX_MESSAGE}</p>
      </div>
      <div>
        <Label>{t('purpose', { defaultValue: 'Purpose' })}</Label>
        <select className="w-full rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2"
          value={purpose} disabled={sending} onChange={(e) => setPurpose(e.target.value as AssetPurpose)}>
          <option value="reference">{t('purpose_reference', { defaultValue: 'Reference' })}</option>
          <option value="deliverable">{t('purpose_deliverable', { defaultValue: 'Deliverable' })}</option>
          <option value="supporting">{t('purpose_supporting', { defaultValue: 'Supporting' })}</option>
        </select>
      </div>
      {problem && problem !== 'none' && (
        <p role="alert" className="text-xs text-danger-strong">{problemText[problem]}</p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={sending}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
        <Button onClick={() => void submit()} disabled={!!problem || sending}>
          {sending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
          {sending ? t('attach_sending', { defaultValue: 'Attaching…' }) : t('attach_submit', { defaultValue: 'Attach' })}
        </Button>
      </div>
    </Modal>
  );
};

const Modal: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/20 p-4" onClick={onClose}>
    <div className="w-full max-w-md rounded-lg bg-background border border-border p-5 space-y-3" onClick={(e) => e.stopPropagation()}>{children}</div>
  </div>
);

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default WorkAssetsSection;
